/* eslint-disable */
/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
// src/pages/ngo/NgoComplianceTracker.tsx

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { CustomXAxisTick } from '../../components/CustomXAxisTick';
import '../../table.css';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

// --- Type Definitions ---
interface ComplianceDocument {
    id: string;
    document_name: string;
    description: string;
    action_type: string;
    responsibility: string;
    step_number: number;
    display_order: number;
    data_fields?: string[] | string;
    rules?: string[] | string;
    can_ngo_upload?: boolean;
    role?: 'company' | 'ngo';
    target_type?: string;
    system_trigger?: string;
    guiding_doc_url?: string | null;
    guiding_doc_filename?: string | null;
    due_date?: string | null;
}

interface ComplianceSubmission {
    id: string;
    ngo_id?: string;
    document_repository_id: string;
    status: string;
    file_name: string;
    file_url: string;
    admin_notes: string | null;
    ngo_notes: string | null;
    submitted_at?: string;
    is_verified?: boolean;
    company_id?: string;
    compliance_status?: string | null;
    expiry_date?: string | null;
}

interface ComplianceItem {
    document: ComplianceDocument;
    submission: ComplianceSubmission | null;
    status: string;
    allSubmissions?: any[];
    request?: any;
    assignment?: any;
}

// --- Main NGO Component ---
const NgoComplianceTracker: React.FC = () => {
    const { userProfile } = useAuth();
  const ngoId = userProfile?.ngo_id;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [checklist, setChecklist] = useState<ComplianceItem[]>([]);
    const [editingItem, setEditingItem] = useState<ComplianceItem | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeSection, setActiveSection] = useState<'ngo' | 'company'>('ngo');
    const [signedUrls, setSignedUrls] = useState<Map<string, string>>(new Map());
    const [uploadRemarks, setUploadRemarks] = useState<string>('');
    const [complianceStatus, setComplianceStatus] = useState<string>('Pending');
    const [validityDate, setValidityDate] = useState<string>('');

    // State to store the associated company name
    const [companyName, setCompanyName] = useState<string>('Company');
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [showDocumentRequestModal, setShowDocumentRequestModal] = useState(false);
    const [documentRequestForm, setDocumentRequestForm] = useState({
        document_name: '',
        description: '',
        due_date: '',
        priority: 'normal' as 'low' | 'normal' | 'high'
    });
    const [documentRequests, setDocumentRequests] = useState<any[]>([]);

    // Helper function to get due date status and color
    const getDueDateStatus = (dueDate: string | null): { status: string; color: string; bgColor: string } => {
        if (!dueDate) {
            return { status: 'No Due Date', color: '#6b7280', bgColor: '#f3f4f615' };
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(dueDate);
        due.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { status: 'Overdue', color: '#ef4444', bgColor: '#ef444415' };
        } else if (diffDays === 0) {
            return { status: 'Due Today', color: '#f59e0b', bgColor: '#f59e0b15' };
        } else if (diffDays <= 3) {
            return { status: 'Due Soon', color: '#f59e0b', bgColor: '#f59e0b15' };
        } else if (diffDays <= 7) {
            return { status: 'Due This Week', color: '#3b82f6', bgColor: '#3b82f615' };
        } else {
            return { status: 'Pending', color: '#10b981', bgColor: '#10b98115' };
        }
    };

    // Fetch NGO-specific data
    const fetchData = useCallback(async (currentNgoId: string) => {
        setLoading(true);
        setError(null);
        try {
            // ── 1. NGO & Company context ──
            const { data: ngoData, error: ngoError } = await supabase
                .from('ngos')
                .select('company_id')
                .eq('id', currentNgoId)
                .single();

            if (ngoError) throw ngoError;

            if (ngoData?.company_id) {
                setCompanyId(ngoData.company_id);
                try {
                    const { data: companyData } = await supabase
                        .from('companies')
                        .select('name')
                        .eq('id', ngoData.company_id)
                        .maybeSingle();
                    setCompanyName(companyData?.name || 'Company');
                } catch {
                    setCompanyName('Company');
                }
            }

            // ── 2. Parallel fetch for core data ──
            const [assignmentsRes, uploadsRes, requestsRes] = await Promise.all([
                supabase
                    .from('document_assignments')
                    .select('*')
                    .eq('ngo_id', currentNgoId),
                supabase
                    .from('document_uploads')
                    .select('*')
                    .eq('ngo_id', currentNgoId)
                    .eq('status', 'active')
                    .order('uploaded_at', { ascending: false }),
                supabase
                    .from('document_requests')
                    .select('*')
                    .or(`requested_by_id.eq.${currentNgoId},requested_to_id.eq.${currentNgoId}`)
                    .order('created_at', { ascending: false })
            ]);

            const assignments = assignmentsRes.data || [];
            const uploads = uploadsRes.data || [];
            const requests = requestsRes.data || [];
            setDocumentRequests(requests);

            // ── 3. Resolve all necessary document_repository IDs (including global compliance docs) ──
            const { data: repoData, error: repoError } = await supabase
                .from('document_repository')
                .select('*')
                .eq('is_active', true)
                .order('display_order', { ascending: true });

            if (repoError) throw repoError;
            const fullRepoDocs = repoData || [];

            // Add any other specific IDs that might not be in the global fetch (e.g., custom requests with step_number=999)
            const docRepoIdSet = new Set<string>();
            assignments.forEach((a: any) => { if (a.document_repository_id) docRepoIdSet.add(a.document_repository_id); });
            uploads.forEach((u: any) => { if (u.document_repository_id) docRepoIdSet.add(u.document_repository_id); });

            const extraIds = Array.from(docRepoIdSet).filter(id => !fullRepoDocs.some((rd: any) => rd.id === id));
            let repoDocs = [...fullRepoDocs];

            if (extraIds.length > 0) {
                const { data: extraRepoDocs } = await supabase
                    .from('document_repository')
                    .select('*')
                    .in('id', extraIds);
                if (extraRepoDocs) {
                    repoDocs = [...repoDocs, ...extraRepoDocs];
                }
            }

            // --- Hybrid Auto-Assignment Logic ---
            const augmentedAssignments = [...assignments];
            repoDocs.forEach(doc => {
                // Auto-assign global NGO documents
                if (doc.role === 'ngo') {
                    // Check if it's explicitly restricted to certain NGOs
                    if (!doc.visible_ngo_ids || (Array.isArray(doc.visible_ngo_ids) && doc.visible_ngo_ids.includes(currentNgoId))) {
                        const exists = augmentedAssignments.find(a => a.document_repository_id === doc.id);
                        if (!exists) {
                            augmentedAssignments.push({
                                id: `auto-ngo-${doc.id}`,
                                document_repository_id: doc.id,
                                ngo_id: currentNgoId,
                                company_id: companyId,
                                status: 'active',
                                created_at: new Date().toISOString(),
                                due_date: doc.due_date || null,
                                is_auto: true
                            });
                        }
                    }
                }
            });

            // Map augmented assignments to Checklist Items
            const items: ComplianceItem[] = [];

            augmentedAssignments.forEach((assignment: any) => {
                const doc = repoDocs.find(d => d.id === assignment.document_repository_id);
                if (!doc) return;

                // Find uploads for this assignment
                const assignmentUploads = uploads.filter((u: any) => 
                    u.document_repository_id === doc.id && 
                    (u.assignment_id === assignment.id || (!u.assignment_id && u.ngo_id === assignment.ngo_id))
                );

                const latestSubmission = assignmentUploads.length > 0
                    ? assignmentUploads.sort((a: any, b: any) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())[0]
                    : null;

                const item: ComplianceItem = {
                    document: doc,
                    assignment: assignment,
                    submission: latestSubmission,
                    status: latestSubmission
                        ? (['Yes', 'Completed'].includes(latestSubmission.compliance_status) ? 'Approved' :
                            (['No', 'In Progress'].includes(latestSubmission.compliance_status) ? 'Rejected' :
                                latestSubmission.compliance_status === 'Pending' ? 'Under Review' :
                                    (latestSubmission.status || 'Submitted')))
                        : 'Not Submitted',
                    allSubmissions: assignmentUploads
                };
                items.push(item);
            });

            const requestsFromNgo = requests.map(req => ({
                document: {
                    id: req.id,
                    document_name: req.document_name,
                    description: req.description || '',
                    action_type: 'Request',
                    responsibility: 'NGO',
                    step_number: 999,
                    display_order: 999,
                    role: 'ngo' as const,
                    can_ngo_upload: false,
                    isRequest: true,
                    due_date: req.due_date || null
                },
                submission: null,
                status: req.status,
                request: req,
                allSubmissions: [],
                assignment: null
            }));

            const combined = [...items, ...requestsFromNgo];
            combined.sort((a, b) => (a.document.display_order || 999) - (b.document.display_order || 999));
            setChecklist(combined);
            
        } catch (err: any) {
            console.error("Fetch Data Error:", err);
            setError(err.message || "An error occurred while fetching compliance data.");
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        if (ngoId) {
            fetchData(ngoId);
        }
    }, [ngoId, fetchData]);

    const ngoChecklist = useMemo(() => checklist.filter(item => item.document.role === 'ngo' || item.document.responsibility?.toLowerCase() === 'ngo' || item.assignment || item.submission), [checklist]);
    const companyChecklist = useMemo(() => checklist.filter(item => item.document.role === 'company' || item.document.responsibility?.toLowerCase() === 'company'), [checklist]);

    // --- Analytics for Charts ---
    const dashboardData = useMemo(() => {
        // Find assigned docs or docs with submissions (Improved matching)
        const myTotalDocs = ngoChecklist.filter(item =>
            item.document.role === 'ngo' ||
            item.document.responsibility?.toLowerCase() === 'ngo' ||
            item.assignment ||
            item.submission
        );
        const myTotal = myTotalDocs.length;
        const myUploaded = myTotalDocs.filter(item => item.submission).length;

        const companyTotalDocs = companyChecklist.filter(item =>
            item.document.role === 'company' ||
            item.document.responsibility?.toLowerCase() === 'company'
        );
        const companyTotal = companyTotalDocs.length;
        const companyUploaded = companyTotalDocs.filter(item => item.submission).length;

        const ngoDocsData = [
            {
                name: 'My Documents',
                'Total Documents': myTotal,
                'Uploaded': myUploaded
            }
        ];

        const companyDocsData = [
            {
                name: companyName,
                'Total Documents': companyTotal,
                'Uploaded': companyUploaded
            }
        ];

        return {
            ngoDocsData,
            companyDocsData,
            myTotal,
            myUploaded,
            companyTotal,
            companyUploaded
        };
    }, [ngoChecklist, companyChecklist, companyName]);

    // Helper function to get signed URL for private bucket files
    const getSignedUrl = async (bucketName: string, filePath: string) => {
        let pathInBucket = filePath;

        if (filePath.includes('/storage/v1/object/public/')) {
            const parts = filePath.split('/storage/v1/object/public/');
            if (parts.length > 1) {
                const afterPublic = parts[1];
                const pathParts = afterPublic.split('/');
                if (pathParts.length > 1 && pathParts[0] === bucketName) {
                    pathInBucket = pathParts.slice(1).join('/');
                } else {
                    pathInBucket = afterPublic.replace(`${bucketName}/`, '');
                }
            }
        }

        if (!pathInBucket || pathInBucket === filePath) {
            if (!filePath.startsWith('http')) pathInBucket = filePath;
            else return null;
        }

        const { data, error } = await supabase.storage.from(bucketName).createSignedUrl(pathInBucket, 60 * 60 * 24 * 365);
        if (error) return null;
        return data.signedUrl;
    };

    const handleFileUpload = async (item: ComplianceItem, file: File | null, remarks?: string, status?: string, validity?: string) => {
        if (!ngoId) return;

        setIsSubmitting(true);
        try {
            let fileUrl = item.submission?.file_url || '';
            let fileName = item.submission?.file_name || '';

            if (file) {
                const storagePath = `${ngoId}/compliance/${item.document.id}_${Date.now()}_${file.name}`;
                const { error: uploadError } = await supabase.storage
                    .from('ngo-documents')
                    .upload(storagePath, file);

                if (uploadError) throw uploadError;

                // Generate signed URL
                const { data: signedData, error: signedError } = await supabase.storage
                    .from('ngo-documents')
                    .createSignedUrl(storagePath, 31536000);

                if (!signedError && signedData?.signedUrl) {
                    fileUrl = signedData.signedUrl;
                } else {
                    const { data: { publicUrl } } = supabase.storage
                        .from('ngo-documents')
                        .getPublicUrl(storagePath);
                    fileUrl = publicUrl;
                }
                fileName = file.name;
            }

            // Check if this is a document request response
            const isRequestResponse = item.request && item.document.id.startsWith('request-');

            if (isRequestResponse && item.request) {
                // For document requests, we need to create or find a document_repository entry
                // First, try to find an existing document_repository entry for this request
                const requestId = item.request.id;
                let documentRepoId: string | null = null;

                // Try to find existing document_repository entry for this request
                const { data: existingRepo } = await supabase
                    .from('document_repository')
                    .select('id')
                    .eq('document_name', item.request.document_name)
                    .eq('category', item.request.category || 'compliance')
                    .eq('role', 'ngo')
                    .limit(1)
                    .single();

                if (existingRepo?.id) {
                    documentRepoId = existingRepo.id;
                } else {
                    // Try to create a new document_repository entry for this request
                    // Use a unique step_number based on request ID to avoid conflicts
                    // We'll use a hash of the request ID to create a unique step_number
                    const stepNumberHash = Math.abs(
                        requestId.split('').reduce((acc: number, char: string) => {
                            return ((acc << 5) - acc) + char.charCodeAt(0);
                        }, 0)
                    ) % 900 + 100; // Generate a number between 100-999

                    const { data: newRepo, error: repoError } = await supabase
                        .from('document_repository')
                        .insert({
                            category: item.request.category || 'compliance',
                            role: 'ngo',
                            step_number: stepNumberHash, // Unique step number based on request ID
                            document_name: item.request.document_name,
                            action_type: 'Upload',
                            description: item.request.description || `Document requested: ${item.request.document_name}`,
                            data_fields: [] as any,
                            responsibility: 'NGO',
                            display_order: 999,
                            is_active: true,
                            visible_to_company: true,
                            visible_to_ngo: true,
                            can_ngo_upload: true
                        })
                        .select('id')
                        .single();

                    if (repoError) {
                        // If it's a unique constraint violation, try to find the existing entry
                        if (repoError.code === '23505' || repoError.message.includes('unique constraint')) {
                            const { data: conflictRepo } = await supabase
                                .from('document_repository')
                                .select('id')
                                .eq('document_name', item.request.document_name)
                                .eq('category', item.request.category || 'compliance')
                                .eq('role', 'ngo')
                                .limit(1)
                                .single();

                            if (conflictRepo?.id) {
                                documentRepoId = conflictRepo.id;
                            } else {
                                throw new Error(`Failed to create document repository entry: ${repoError.message}`);
                            }
                        } else {
                            throw new Error(`Failed to create document repository entry: ${repoError.message}`);
                        }
                    } else if (newRepo?.id) {
                        documentRepoId = newRepo.id;
                    } else {
                        throw new Error('Failed to create document repository entry');
                    }
                }

                // Now insert the upload with the valid document_repository_id
                const submissionData: any = {
                    ngo_id: ngoId,
                    document_repository_id: documentRepoId,
                    file_name: fileName,
                    file_url: fileUrl,
                    compliance_status: status || 'Pending',
                    expiry_date: validity ? validity : null,
                    status: 'active',
                    notes: remarks ? `Uploaded in response to document request ID: ${requestId}. Remarks: ${remarks}` : `Uploaded in response to document request ID: ${requestId}`,
                    assignment_id: (item.assignment?.id && !String(item.assignment.id).startsWith('auto-')) ? item.assignment.id : null
                };

                // Insert the upload
                const { error: insertError } = await supabase
                    .from('document_uploads')
                    .insert(submissionData);
                if (insertError) throw insertError;

                // Update the document request status to 'completed'
                const { error: updateRequestError } = await supabase
                    .from('document_requests')
                    .update({ status: 'completed' })
                    .eq('id', requestId);
                if (updateRequestError) {
                    console.warn('Could not update request status:', updateRequestError);
                }
            } else {
                // Regular document upload
                const submissionData: any = {
                    ngo_id: ngoId,
                    document_repository_id: item.document.id,
                    file_name: fileName,
                    file_url: fileUrl,
                    status: 'active',
                    compliance_status: status || 'Pending',
                    expiry_date: validity ? validity : null,
                    notes: remarks || null,
                    assignment_id: (item.assignment?.id && !String(item.assignment.id).startsWith('auto-')) ? item.assignment.id : null
                };

                if (item.submission) {
                    const { error: updateError } = await supabase
                        .from('document_uploads')
                        .update(submissionData)
                        .eq('id', item.submission.id);
                    if (updateError) throw updateError;
                } else {
                    const { error: insertError } = await supabase
                        .from('document_uploads')
                        .insert(submissionData);
                    if (insertError) throw insertError;
                }
            }

            await fetchData(ngoId);
            setEditingItem(null);
            setUploadRemarks('');
        } catch (err: any) {
            console.error('Error uploading file:', err);
            alert(`Error: ${err.message || 'Failed to upload file'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <>
            <div className="compliance-container">
                <div style={{ marginBottom: '2rem' }}>
                    <h1 style={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: 'var(--text)',
                        marginBottom: '0.5rem',
                        letterSpacing: '-0.5px'
                    }}>
                        Compliance Management
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                        Upload and manage your compliance documents and submissions
                    </p>
                </div>

                {error && (
                    <div className="toast toast-error" style={{ marginBottom: '1.5rem' }}>
                        {error}
                    </div>
                )}

                {/* Dashboard Overview */}
                <div className="card" style={{ padding: '2rem', marginBottom: '2rem' }}>
                    <h2 style={{
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: 'var(--text)',
                        marginBottom: '1.5rem'
                    }}>
                        Dashboard Overview
                    </h2>

                    {/* Charts in flex column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

                        {/* Graph 1: NGO Documents (Total vs Uploaded) */}
                        <div className="card" style={{ padding: '1.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, color: 'var(--text)' }}>
                                    My Documents Status
                                </h3>
                                <span style={{ fontSize: '1rem', fontWeight: 600, color: '#10b981' }}>
                                    {dashboardData.myUploaded} of {dashboardData.myTotal} Complied
                                </span>
                            </div>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={dashboardData.ngoDocsData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" interval={0} tick={<CustomXAxisTick />} height={60} />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend verticalAlign="top" />
                                    <Bar dataKey="Uploaded" fill="#10b981" />
                                    <Bar dataKey="Total Documents" fill="#3b82f6" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>


                    </div>
                </div>

                {/* Compliance Checklist */}
                <div className="card" style={{ padding: '2rem' }}>
                    <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <h2 style={{
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                color: 'var(--text)',
                                margin: 0,
                                marginBottom: '0.5rem'
                            }}>
                                Compliance Checklist
                            </h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                                {checklist.length} requirement{checklist.length !== 1 ? 's' : ''} found
                            </p>
                        </div>

                    </div>

                    <div className="card" style={{ marginBottom: '1.5rem' }}>
                        <h2 style={{ marginBottom: '1rem' }}>
                            Compliance Documents Flow
                        </h2>
                        <p style={{ color: 'var(--muted)', marginBottom: '1.5rem' }}>
                            Follow the steps below to understand the complete workflow and upload required documents. Documents are organized by responsibility (Company or NGO).
                        </p>
                    </div>


                    {/* NGO Documents Section */}
                    {dashboardData.myTotal === 0 ? (
                        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
                            <p style={{ color: 'var(--muted)' }}>No assigned NGO compliance documents required.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gap: '1.5rem' }}>
                            {ngoChecklist.filter(i => (i as any).assignment || i.submission || (i.request && i.request.requested_to_type === 'ngo')).map((item: any) => {
                                // Check if this is a document request
                                const isRequest = item.request || (item.document as any).isRequest;
                                const request = item.request;
                                const dueDateStatus = isRequest && request?.due_date ? getDueDateStatus(request.due_date) : null;

                                const dataFields = typeof item.document.data_fields === 'string'
                                    ? JSON.parse(item.document.data_fields || '[]')
                                    : (item.document.data_fields || []);
                                const rules = typeof item.document.rules === 'string'
                                    ? JSON.parse(item.document.rules || '[]')
                                    : (item.document.rules || []);


                                return (
                                    <div
                                        key={item.document.id}
                                        className="card compliance-item-card"
                                        style={{
                                            position: 'relative',
                                            paddingLeft: '3rem',
                                            border: isRequest ? `2px solid ${dueDateStatus?.color || '#f59e0b'}` : undefined
                                        }}
                                    >
                                        {/* Step Number Badge */}
                                        <div 
                                            className="compliance-step-badge"
                                            style={{
                                                position: 'absolute', left: '1rem', top: '1rem', width: '2rem', height: '2rem',
                                                borderRadius: '50%',
                                                background: isRequest ? (dueDateStatus?.color || '#f59e0b') : 'var(--primary)',
                                                color: 'white',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontWeight: '600', fontSize: '0.875rem'
                                            }}
                                        >
                                            {isRequest ? '!' : (item.document.display_order || item.document.step_number)}
                                        </div>

                                        {/* Document Header */}
                                        <div style={{ marginBottom: '0.75rem' }}>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                                                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)' }}>
                                                    {item.document.document_name}
                                                </h3>

                                                {isRequest && (
                                                    <span style={{
                                                        padding: '0.25rem 0.75rem',
                                                        borderRadius: '12px',
                                                        fontSize: '0.75rem',
                                                        backgroundColor: '#f59e0b15',
                                                        color: '#f59e0b',
                                                        fontWeight: '600'
                                                    }}>
                                                        Document Request
                                                    </span>
                                                )}

                                                {dueDateStatus && (
                                                    <span style={{
                                                        padding: '0.25rem 0.75rem',
                                                        borderRadius: '12px',
                                                        fontSize: '0.75rem',
                                                        backgroundColor: dueDateStatus.bgColor,
                                                        color: dueDateStatus.color,
                                                        fontWeight: '600'
                                                    }}>
                                                        {dueDateStatus.status}
                                                    </span>
                                                )}

                                                <span style={{
                                                    padding: '0.375rem 0.875rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, display: 'inline-block',
                                                    backgroundColor: item.status === 'Approved' || item.status === 'Completed' ? '#10b98115' :
                                                        item.status === 'Rejected' ? '#ef444415' :
                                                            item.status === 'completed' ? '#10b98115' :
                                                                item.status === 'cancelled' ? '#6b728015' :
                                                                    '#3b82f615',
                                                    color: item.status === 'Approved' || item.status === 'Completed' ? '#10b981' :
                                                        item.status === 'Rejected' ? '#ef4444' :
                                                            item.status === 'completed' ? '#10b981' :
                                                                item.status === 'cancelled' ? '#6b7280' :
                                                                    '#3b82f6'
                                                }}>
                                                    {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Due Date Display */}
                                        {item.document.due_date && (
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <span style={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: new Date(item.document.due_date) < new Date() ? '#ef4444' : '#f59e0b',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    📅 Due: {new Date(item.document.due_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                    {new Date(item.document.due_date) < new Date() && <span style={{ fontSize: '0.7rem' }}>(Overdue)</span>}
                                                </span>
                                            </div>
                                        )}

                                        {item.document.description && (
                                            <p style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                                                {item.document.description}
                                            </p>
                                        )}

                                        {/* Guiding Document */}
                                        {item.document.guiding_doc_url && (
                                            <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #10b981' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        <strong style={{ fontSize: '0.875rem', color: '#065f46' }}>📄 Guiding Document:</strong>
                                                        <p style={{ fontSize: '0.75rem', color: '#047857', margin: '0.25rem 0 0 0' }}>
                                                            {item.document.guiding_doc_filename || 'Guideline Document'}
                                                        </p>
                                                    </div>
                                                    <a
                                                        href={item.document.guiding_doc_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm"
                                                        style={{
                                                            fontSize: '0.8rem',
                                                            backgroundColor: '#10b981',
                                                            color: 'white',
                                                            border: 'none'
                                                        }}
                                                    >
                                                        View Guide
                                                    </a>
                                                </div>
                                            </div>
                                        )}

                                        {/* Show request details if it's a request */}
                                        {isRequest && request && (
                                            <div style={{ marginTop: '1rem', padding: '1rem', background: dueDateStatus?.bgColor || '#fef3c7', borderRadius: '8px', border: `1px solid ${dueDateStatus?.color || '#f59e0b'}` }}>
                                                <p style={{ fontSize: '0.875rem', color: dueDateStatus?.color || '#92400e', margin: 0 }}>
                                                    <strong>Status:</strong> {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                                    {request.due_date && (
                                                        <> • <strong>Due Date:</strong> {new Date(request.due_date).toLocaleDateString('en-IN', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric'
                                                        })}</>
                                                    )}
                                                    {request.priority && (
                                                        <> • <strong>Priority:</strong> {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}</>
                                                    )}
                                                </p>
                                            </div>
                                        )}

                                        {/* Upload Section for Regular Documents */}
                                        {!isRequest && (item.document.can_ngo_upload || item.assignment) && (
                                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                                    <strong style={{ fontSize: '0.875rem' }}>
                                                        Uploaded Documents ({item.submission ? 1 : 0})
                                                    </strong>
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => setEditingItem(item)}
                                                        style={{ fontSize: '0.8rem' }}
                                                    >
                                                        + Upload Document
                                                    </button>
                                                </div>
                                                {item.submission && (() => {
                                                    let isExpired = false;
                                                    let isExpiringSoon = false;
                                                    if (item.submission.expiry_date) {
                                                        const daysLeft = Math.ceil((new Date(item.submission.expiry_date).getTime() - Date.now()) / (1000 * 3600 * 24));
                                                        if (daysLeft <= 0) isExpired = true;
                                                        else if (daysLeft <= 15) isExpiringSoon = true;
                                                    }
                                                    const isHighlight = isExpired || isExpiringSoon;
                                                    const hasNotes = item.submission.admin_notes && item.submission.admin_notes !== item.submission.ngo_notes;
                                                    const bgColor = isHighlight ? '#fee2e2' : (hasNotes ? '#fef3c7' : 'white');
                                                    const borderColor = isHighlight ? '#f87171' : (hasNotes ? '#f59e0b' : 'var(--border)');

                                                    return (
                                                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                                                            <div 
                                                                className="compliance-file-row"
                                                                style={{
                                                                    padding: '0.75rem',
                                                                    background: bgColor,
                                                                    borderRadius: '6px',
                                                                    border: `1px solid ${borderColor}`
                                                                }}
                                                            >
                                                                <div style={{ flex: 1 }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                                                                        <strong style={{ fontSize: '0.875rem', color: isHighlight ? '#991b1b' : 'inherit' }}>{item.submission.file_name}</strong>
                                                                        {isExpired && <span style={{ padding: '0.125rem 0.5rem', borderRadius: '12px', fontSize: '0.7rem', backgroundColor: '#ef4444', color: 'white', fontWeight: '600' }}>Expired</span>}
                                                                        {isExpiringSoon && <span style={{ padding: '0.125rem 0.5rem', borderRadius: '12px', fontSize: '0.7rem', backgroundColor: '#f59e0b', color: 'white', fontWeight: '600' }}>Expires Soon</span>}
                                                                                <span style={{
                                                                                    padding: '0.125rem 0.5rem',
                                                                                    borderRadius: '12px',
                                                                                    fontSize: '0.7rem',
                                                                                    backgroundColor: ['Yes', 'Completed'].includes(item.submission.compliance_status) ? '#10b981' :
                                                                                        (['No', 'In Progress'].includes(item.submission.compliance_status)) ? '#facc15' :
                                                                                            ['Pending', 'N/A', 'Not Applicable'].includes(item.submission.compliance_status) ? '#94a3b8' : '#94a3b8',
                                                                                    color: 'white',
                                                                                    fontWeight: '600'
                                                                                }}>
                                                                                    {['Yes', 'Completed'].includes(item.submission.compliance_status) ? 'Completed' : item.submission.compliance_status}
                                                                                </span>
                                                                        {item.submission.admin_notes && item.submission.admin_notes !== item.submission.ngo_notes && (
                                                                            <span style={{
                                                                                padding: '0.125rem 0.5rem',
                                                                                borderRadius: '12px',
                                                                                fontSize: '0.7rem',
                                                                                backgroundColor: '#f59e0b',
                                                                                color: 'white',
                                                                                fontWeight: '600'
                                                                            }}>
                                                                                Changes Requested
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0.25rem 0 0 0' }}>
                                                                        Updated {new Date(item.submission.submitted_at || '').toLocaleDateString('en-IN', {
                                                                            year: 'numeric',
                                                                            month: 'short',
                                                                            day: 'numeric'
                                                                        })}
                                                                        {item.submission.expiry_date && ` • Valid till: ${new Date(item.submission.expiry_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}`}
                                                                    </p>
                                                                    {item.submission.ngo_notes && (
                                                                        <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#ecfdf5', borderRadius: '4px', border: '1px solid #10b981' }}>
                                                                            <strong style={{ fontSize: '0.75rem', color: '#065f46' }}>Your Remarks:</strong>
                                                                            <p style={{ fontSize: '0.75rem', color: '#047857', margin: '0.25rem 0 0 0' }}>
                                                                                {item.submission.ngo_notes}
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                    {item.submission.admin_notes && item.submission.admin_notes !== item.submission.ngo_notes && (
                                                                        <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'white', borderRadius: '4px', border: '1px solid #f59e0b' }}>
                                                                            <strong style={{ fontSize: '0.75rem', color: '#92400e' }}>Company Feedback:</strong>
                                                                            <p style={{ fontSize: '0.75rem', color: '#92400e', margin: '0.25rem 0 0 0' }}>
                                                                                {item.submission.admin_notes}
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="compliance-actions-row" style={{ display: 'flex', gap: '0.5rem' }}>
                                                                    <a
                                                                        href={signedUrls.get(item.submission.id) || item.submission.file_url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-sm btn-outline"
                                                                        style={{ fontSize: '0.8rem' }}
                                                                    >
                                                                        View
                                                                    </a>
                                                                    <a
                                                                        href={signedUrls.get(item.submission.id) || item.submission.file_url}
                                                                        download
                                                                        className="btn btn-sm btn-secondary"
                                                                        style={{ fontSize: '0.8rem' }}
                                                                    >
                                                                        Download
                                                                    </a>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            setEditingItem(item);
                                                                        }}
                                                                        className="btn btn-sm btn-outline"
                                                                        style={{ fontSize: '0.8rem', borderColor: '#3b82f6', color: '#3b82f6' }}
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm"
                                                                        style={{
                                                                            fontSize: '0.8rem',
                                                                            backgroundColor: '#ef4444',
                                                                            color: 'white',
                                                                            border: 'none'
                                                                        }}
                                                                        onClick={async (e) => {
                                                                            e.preventDefault();
                                                                            if (!window.confirm('Are you sure you want to delete this document?')) return;
                                                                            setIsSubmitting(true);
                                                                            try {
                                                                                // Delete from storage
                                                                                const filePath = item.submission.file_url;
                                                                                const bucket = filePath.includes('/company-documents/') || filePath.includes('company-documents/') ? 'company-documents' : 'ngo-documents';
                                                                                let pathInBucket: string | null = null;

                                                                                if (filePath.includes('/storage/v1/object/public/')) {
                                                                                    const parts = filePath.split(`/storage/v1/object/public/${bucket}/`);
                                                                                    if (parts.length > 1) {
                                                                                        pathInBucket = parts[1].split('?')[0];
                                                                                    }
                                                                                } else if (filePath.includes('/storage/v1/object/sign/')) {
                                                                                    const parts = filePath.split(`/storage/v1/object/sign/${bucket}/`);
                                                                                    if (parts.length > 1) {
                                                                                        pathInBucket = parts[1].split('?')[0];
                                                                                    }
                                                                                }

                                                                                if (pathInBucket) {
                                                                                    const { error: storageError } = await supabase.storage
                                                                                        .from(bucket)
                                                                                        .remove([decodeURIComponent(pathInBucket)]);
                                                                                    if (storageError) {
                                                                                        console.warn('Storage delete warning (continuing):', storageError.message);
                                                                                    }
                                                                                }

                                                                                // Delete from database
                                                                                const { error: deleteError } = await supabase
                                                                                    .from('document_uploads')
                                                                                    .delete()
                                                                                    .eq('id', item.submission.id);

                                                                                if (deleteError) throw deleteError;

                                                                                alert('Document deleted successfully!');
                                                                                if (ngoId) await fetchData(ngoId);
                                                                            } catch (err: any) {
                                                                                console.error('Error deleting document:', err);
                                                                                alert(`Error: ${err.message || 'Failed to delete document'}`);
                                                                            } finally {
                                                                                setIsSubmitting(false);
                                                                            }
                                                                        }}
                                                                        disabled={isSubmitting}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        )}

                                        {/* Upload Section for Document Requests (NGO can upload in response) */}
                                        {isRequest && request && request.requested_to_type === 'ngo' && request.requested_to_id === ngoId && (
                                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                                    <strong style={{ fontSize: '0.875rem', color: '#92400e' }}>
                                                        Upload Document in Response
                                                    </strong>
                                                    <button
                                                        className="btn btn-sm"
                                                        onClick={() => {
                                                            // Create a temporary item for upload
                                                            const uploadItem: ComplianceItem = {
                                                                document: {
                                                                    id: `request-${request.id}`,
                                                                    document_name: request.document_name,
                                                                    description: request.description || '',
                                                                    action_type: 'Upload',
                                                                    responsibility: 'NGO',
                                                                    step_number: 999,
                                                                    display_order: 999,
                                                                    can_ngo_upload: true,
                                                                    role: 'ngo'
                                                                },
                                                                submission: null,
                                                                status: 'pending',
                                                                request: request
                                                            };
                                                            setEditingItem(uploadItem);
                                                        }}
                                                        style={{
                                                            fontSize: '0.8rem',
                                                            backgroundColor: '#f59e0b',
                                                            color: 'white',
                                                            border: 'none'
                                                        }}
                                                    >
                                                        + Upload Document
                                                    </button>
                                                </div>
                                                <p style={{ fontSize: '0.75rem', color: '#92400e', margin: 0 }}>
                                                    Upload the requested document to fulfill this request from {companyName}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>


                {/* Create Document Request Modal */}
                {showDocumentRequestModal && (
                    <div className="modal-overlay" onClick={() => setShowDocumentRequestModal(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                            <div style={{
                                marginBottom: '1.5rem',
                                paddingBottom: '1rem',
                                borderBottom: '2px solid var(--border-light)'
                            }}>
                                <h2 style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    color: 'var(--text)',
                                    margin: 0,
                                    marginBottom: '0.25rem'
                                 }}>
                                    Create Document Request for Company
                                </h2>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                                    Request a specific document from {companyName}
                                </p>
                            </div>
                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                if (!ngoId || !companyId) return;
                                setIsSubmitting(true);
                                try {
                                    const { data: newRequest, error: requestError } = await supabase
                                        .from('document_requests')
                                        .insert({
                                            requested_by_type: 'ngo',
                                            requested_by_id: ngoId,
                                            requested_to_type: 'company',
                                            requested_to_id: companyId,
                                            document_name: documentRequestForm.document_name,
                                            description: documentRequestForm.description || `Document request from NGO`,
                                            category: 'compliance',
                                            due_date: documentRequestForm.due_date || null,
                                            priority: documentRequestForm.priority,
                                            status: 'pending'
                                        })
                                        .select()
                                        .single();

                                    if (requestError) throw requestError;

                                    alert('Document request created successfully! The company will see this request.');
                                    setShowDocumentRequestModal(false);
                                    setDocumentRequestForm({
                                        document_name: '',
                                        description: '',
                                        due_date: '',
                                        priority: 'normal'
                                    });
                                    if (ngoId) fetchData(ngoId);
                                } catch (err: any) {
                                    console.error('Error creating document request:', err);
                                    alert(err.message || 'Failed to create document request');
                                } finally {
                                    setIsSubmitting(false);
                                }
                            }}>
                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                        Document Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={documentRequestForm.document_name}
                                        onChange={(e) => setDocumentRequestForm({ ...documentRequestForm, document_name: e.target.value })}
                                        required
                                        placeholder="e.g., Additional Company Certificate"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid var(--border)',
                                            borderRadius: 'var(--radius-md)',
                                            fontSize: '0.875rem'
                                        }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                        Description
                                    </label>
                                    <textarea
                                        value={documentRequestForm.description}
                                        onChange={(e) => setDocumentRequestForm({ ...documentRequestForm, description: e.target.value })}
                                        placeholder="Describe what document is needed and why..."
                                        rows={4}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid var(--border)',
                                            borderRadius: 'var(--radius-md)',
                                            fontSize: '0.875rem',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                        Due Date (Optional)
                                    </label>
                                    <input
                                        type="date"
                                        value={documentRequestForm.due_date}
                                        onChange={(e) => setDocumentRequestForm({ ...documentRequestForm, due_date: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid var(--border)',
                                            borderRadius: 'var(--radius-md)',
                                            fontSize: '0.875rem'
                                        }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                        Priority
                                    </label>
                                    <select
                                        value={documentRequestForm.priority}
                                        onChange={(e) => setDocumentRequestForm({ ...documentRequestForm, priority: e.target.value as 'low' | 'normal' | 'high' })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid var(--border)',
                                            borderRadius: 'var(--radius-md)',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <option value="low">Low</option>
                                        <option value="normal">Normal</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>

                                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        className="btn btn-outline"
                                        onClick={() => {
                                            setShowDocumentRequestModal(false);
                                            setDocumentRequestForm({
                                                document_name: '',
                                                description: '',
                                                due_date: '',
                                                priority: 'normal'
                                            });
                                        }}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Creating...' : 'Create Request'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Upload Modal */}
                {editingItem && (
                    <div className="modal-overlay" onClick={() => setEditingItem(null)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                            <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '2px solid var(--border-light)' }}>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                                    {editingItem.submission ? 'Update' : 'Upload'} Compliance Document
                                </h2>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                                    {editingItem.document.document_name}
                                </p>
                            </div>

                            {editingItem.submission && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                        Current File
                                    </label>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <a
                                            href={signedUrls.get(editingItem.submission.id) || editingItem.submission.file_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-outline"
                                            style={{ fontSize: '0.875rem' }}
                                        >
                                            View
                                        </a>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const fileInput = (e.target as HTMLFormElement).querySelector('input[type="file"]') as HTMLInputElement;
                                if (fileInput?.files?.[0] || editingItem.submission) {
                                    handleFileUpload(editingItem, fileInput.files?.[0] || null, uploadRemarks, complianceStatus, validityDate);
                                } else {
                                    alert('Please select a file to upload');
                                }
                            }}>
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Status *</label>
                                    <select
                                        value={complianceStatus}
                                        onChange={(e) => setComplianceStatus(e.target.value)}
                                        required
                                        style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem' }}
                                    >
                                        <option value="Completed">Completed</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Not Applicable">Not Applicable</option>
                                    </select>
                                </div>
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Upload File {editingItem.submission ? '(Optional if not changing)' : '*'}</label>
                                    <input
                                        type="file"
                                        required={!editingItem.submission}
                                        style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}
                                    />
                                    {editingItem.submission && (
                                        <p style={{ fontSize: '0.75rem', color: 'var(--primary)', marginTop: '0.5rem' }}>
                                            Currently uploaded: {editingItem.submission.file_name}
                                        </p>
                                    )}
                                </div>
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Validity</label>
                                    <input
                                        type="date"
                                        value={validityDate}
                                        onChange={(e) => setValidityDate(e.target.value)}
                                        style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem' }}
                                    />
                                </div>
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Remarks</label>
                                    <textarea
                                        value={uploadRemarks}
                                        onChange={(e) => setUploadRemarks(e.target.value)}
                                        placeholder="Add any notes about this document..."
                                        style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', minHeight: '80px', fontSize: '0.875rem' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                                    <button type="button" className="btn btn-outline" onClick={() => setEditingItem(null)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                        {isSubmitting ? 'Uploading...' : editingItem.submission ? 'Update' : 'Upload'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export { NgoComplianceTracker as NGOComplianceTracker };
export default NgoComplianceTracker;
